/**
 * The real height of the visible page as `--app-h` (D-089). iOS 26 home-screen apps can report `100vh`/`100dvh`
 * taller than the screen (an app installed before the full-screen tags, WebKit bug 316008), which pushed the table's
 * bottom bar and the results button off the bottom. The window's own measured height is right, so layouts use
 * `var(--app-h, 100dvh)` and fall back to `100dvh` before this runs.
 */
export function trackViewportHeight(win: Window = window): () => void {
  const root = win.document.documentElement;
  const set = (): void => {
    const h = Math.min(win.innerHeight, root.clientHeight || win.innerHeight);
    if (h > 0) root.style.setProperty("--app-h", `${Math.round(h)}px`);
  };
  set();
  const later = (): void => { set(); win.setTimeout(set, 250); };
  win.addEventListener("resize", set);
  win.addEventListener("orientationchange", later);
  win.addEventListener("pageshow", later);
  return () => { win.removeEventListener("resize", set); win.removeEventListener("orientationchange", later); win.removeEventListener("pageshow", later); };
}

/** Measured sizes for Settings › Help › Screen check (D-090), so a cut-off screen can be reported with numbers. */
export function screenCheck(win: Window = window): string {
  const doc = win.document, probe = doc.createElement("div");
  probe.style.cssText = "position:fixed;left:-9999px;top:0;width:1px;visibility:hidden;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)";
  doc.body.appendChild(probe);
  const unit = (u: string): number => { probe.style.height = `100${u}`; return Math.round(probe.getBoundingClientRect().height - parseFloat(getComputedStyle(probe).paddingTop) - parseFloat(getComputedStyle(probe).paddingBottom)); };
  const top = Math.round(parseFloat(getComputedStyle(probe).paddingTop)), bottom = Math.round(parseFloat(getComputedStyle(probe).paddingBottom));
  const out = `screen ${win.screen.width}×${win.screen.height}, window ${win.innerWidth}×${win.innerHeight}, page ${doc.documentElement.clientHeight}, vh ${unit("vh")}, dvh ${unit("dvh")}, lvh ${unit("lvh")}, top ${top}, bottom ${bottom}, installed ${win.matchMedia?.("(display-mode: standalone)").matches || (win.navigator as Navigator & { standalone?: boolean }).standalone ? "yes" : "no"}`;
  probe.remove();
  return out;
}
